import axios from "axios";
import {API_BASE_URL} from "../config/api";
import {ConnectionMode, TravelProfile} from "../models/flightSearchData";
import {TravelRequirements} from "../models/RoundTripFlightMatch";
import {AirportService} from "./AirportService";

type CountryStatuses = Record<string, TravelRequirements["status"]>;
const requests = new Map<string, Promise<CountryStatuses>>();

export function matchesVisaFilter(status: TravelRequirements["status"], profile: TravelProfile) {
  if (!profile.visaFreeOnly && !profile.easyVisaOnly) return true;
  if (status === "visa_free" || status === "entry_right_assumed") return true;
  return !profile.visaFreeOnly && !!profile.easyVisaOnly && ["evisa", "eta", "visa_on_arrival"].includes(status);
}

export class TravelRequirementsService {
  static getCountryStatuses(passport: string): Promise<CountryStatuses> {
    let request = requests.get(passport);
    if (!request) {
      request = axios.get<CountryStatuses>(`${API_BASE_URL}/travel-requirements/${encodeURIComponent(passport)}`)
        .then(response => response.data).catch(error => {requests.delete(passport); throw error;});
      requests.set(passport, request);
    }
    return request;
  }

  static async eligibleDestinations(profile: TravelProfile, departures: string[], mode: ConnectionMode): Promise<Set<string>> {
    const [statuses, airports, routes] = await Promise.all([
      this.getCountryStatuses(profile.passportCountry), AirportService.getAirports(),
      Promise.all(departures.map(code => AirportService.getReachableDestinationAirportsForDepartures([code], mode))),
    ]);
    const countryByAirport = new Map(airports.map(airport => [airport.iata, airport.country_code.toUpperCase()]));
    const eligible = new Set<string>();
    routes.forEach((destinations, index) => {
      const origin = countryByAirport.get(departures[index]);
      destinations.forEach(destination => {
        const country = destination.country_code.toUpperCase();
        const status = origin && origin === country ? "entry_right_assumed" : statuses[country] || "unknown";
        if (matchesVisaFilter(status, profile)) eligible.add(destination.iata);
      });
    });
    // This is a country-level preview. The backend checks actual trip duration and transit.
    return eligible;
  }
}
